/**
 * Какво казва КАСАТА за дневника си — единственият източник за двата въпроса,
 * от които зависят и лентата, и връщането в облака (§15.5 и §17.1 на
 * договора, docs/edge.md):
 *
 *  • **изпратила ли е всичко** (`synced` + `mode`) — терминалът не бива да се
 *    върне в облака, докато масите, отворени без връзка, живеят само в
 *    дневника ѝ;
 *  • **има ли НЕРАЗРЕШЕНИ действия** (`unresolved`) — такива, които облакът е
 *    отказал и които касата НЕ може да качи сама: чакат човек при нея
 *    („Опитай пак" / „Отпиши" в портала ѝ). Те са отделен брояч от чакащите
 *    (`pending_events`) точно защото значат различно нещо, а преди §17.1
 *    „няма чакащи" минаваше за „всичко е горе" — и продажба, отказана от
 *    облака, изчезваше и от двете места.
 *
 * Модулът само ПИТА и записва отговора в Redux (`edgeSlice`); решението какво
 * да прави с него е на `edgeMode.ts` — единственият, който сменя режима.
 */
import { getEdgeStatus, getEdgeSyncState } from "../../api/edge";
import {
  setEdgeManualOverride,
  setEdgeReservationsAsOf,
  setEdgeSynced,
  setEdgeUnresolved,
} from "../../store/edgeSlice";
import {
  devLog,
  getActiveEdgeTicket,
  getEdgeRuntime,
  warnLog,
} from "./edgeRuntime";
import type { EdgeMode } from "./edgeState";

/** Отговорът на касата, сведен до нещата, които FE-то ползва. */
export interface EdgeSyncSnapshot {
  synced: boolean;
  mode: EdgeMode;
  unresolved: number;
  /**
   * Касата е задържана локално по РЪЧНО решение (§19.5) — няма да излезе
   * сама, изходът е `POST {edge}/mode/leave`. `false` при по-стара каса и по
   * резервния път през `/status` (той не го носи).
   */
  manualOverride: boolean;
  /** Решението е натиснато от ТОВА устройство — само то го отменя само. */
  manualOverrideByThisDevice: boolean;
  /**
   * Към кой момент са резервациите на касата (`reservations_as_of`, ISO) —
   * подзаглавието на „Резервации" в локален режим. `null` при каса без
   * резервации и по резервния път през `/status`.
   */
  reservationsAsOf?: string | null;
}

/**
 * Може ли терминалът да се върне в облака. И трите условия са НЕОБХОДИМИ:
 * касата е изпратила дневника си, сама е излязла от локален режим и НЕ държи
 * нищо неразрешено. Третото е застраховка: §17.1 иска `synced` да пада и от
 * неразрешените, но каса, която още го смята по стария начин (само по
 * чакащите), не бива да изтегли терминала в облака.
 */
export const isEdgeFullySynced = (snapshot: EdgeSyncSnapshot): boolean =>
  snapshot.synced && snapshot.mode === "cloud" && snapshot.unresolved === 0;

/** Броячът от касата: по-стара каса не го праща, боклук не се брои. */
function readUnresolvedCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

// Предупреждението за неразрешени се логва при ПРОМЯНА на броя, не на всяка
// сонда (тя тече на 5 s) — иначе консолата на терминала става безполезна.
let lastLoggedUnresolved = 0;

/**
 * Пита касата и записва отговора в Redux — `synced` (лентата казва „изчаква
 * се касата") и `unresolved` (лентата предупреждава „N действия не са
 * стигнали до облака"). Вика се на ВСЯКА сонда в локален режим, а не чак
 * когато облакът се върне: човекът на смяна трябва да види предупреждението
 * веднага, а не след като интернетът се появи.
 *
 * Връща `null`, когато касата мълчи — броячът на неуспехите в `edgeMode.ts`
 * решава дали да се върне без проверката.
 */
export async function refreshEdgeSyncState(
  base: string | null,
): Promise<EdgeSyncSnapshot | null> {
  if (!base) return null;
  const snapshot = await fetchEdgeSyncStateSafe(base);
  const runtime = getEdgeRuntime();
  if (!snapshot || !runtime) return snapshot;
  runtime.store.dispatch(setEdgeSynced(isEdgeFullySynced(snapshot)));
  runtime.store.dispatch(setEdgeUnresolved(snapshot.unresolved));
  runtime.store.dispatch(setEdgeManualOverride(snapshot.manualOverride));
  runtime.store.dispatch(setEdgeReservationsAsOf(snapshot.reservationsAsOf ?? null));
  if (snapshot.unresolved !== lastLoggedUnresolved) {
    lastLoggedUnresolved = snapshot.unresolved;
    if (snapshot.unresolved > 0) {
      warnLog(
        `the till holds ${snapshot.unresolved} unresolved event(s) — staying in local mode until a human clears them`,
      );
    }
  }
  return snapshot;
}

/** При излизане от локален режим: следващият престой да логва наново. */
export function forgetEdgeSyncState() {
  lastLoggedUnresolved = 0;
}

/**
 * `GET {edge}/sync-state` — публичният endpoint с чакащите, неразрешените и
 * `synced`, защото точно терминалът БЕЗ билет (изгасен през нощта, изтекъл
 * билет) не бива да се върне в облака, докато касата държи неизпратен
 * дневник; пълният `/status` иска билет и там би върнал 401.
 *
 * Стара каса (без `sync-state`) отговаря 404 → пада се към `/status` с
 * билета, ако има такъв.
 */
async function fetchEdgeSyncStateSafe(
  base: string,
): Promise<EdgeSyncSnapshot | null> {
  const ticket = getActiveEdgeTicket();
  try {
    const state = await getEdgeSyncState(base, ticket);
    if (isEdgeAnswer(state)) {
      return {
        ...toSnapshot(state, state.unresolved, {
          manualOverride: state.manual_override === true,
          manualOverrideByThisDevice:
            state.manual_override === true &&
            state.manual_override_by_this_device === true,
        }),
        reservationsAsOf:
          typeof state.reservations_as_of === "string"
            ? state.reservations_as_of
            : null,
      };
    }
    warnLog(NOT_AN_EDGE_LOG);
  } catch (error) {
    devLog("edge sync-state failed", error);
  }
  if (!ticket) return null;
  try {
    const status = await getEdgeStatus(base, ticket);
    if (isEdgeAnswer(status)) return toSnapshot(status, status.unresolved_events);
    warnLog(NOT_AN_EDGE_LOG);
    return null;
  } catch (error) {
    devLog("edge status failed", error);
    return null;
  }
}

const NOT_AN_EDGE_LOG =
  "the till answered 200 with a body that is not an edge status — treating it as silent";

/**
 * Отговор от КАСАТА ли е това изобщо. Каса, върната на билд БЕЗ локален режим
 * (rollback на обновяването), отговаря на `/edge/sync-state` с **200 и HTML-а
 * на своя панел** — catch-all route-ът ѝ хваща непознатия път. axios не хвърля,
 * `data` е низ, а `state.synced` е `undefined`.
 *
 * Без тази проверка такъв низ минаваше за валиден отговор със `synced: false`:
 * `edgeMode.leaveIfEdgeSynced` вижда снимка (не `null`), нулира брояча на
 * неуспешните сонди, аварийният изход НЕ тръгва и терминалът остава в локален
 * режим завинаги, макар облакът да е жив. Измерено срещу истинска каса 1.77.0:
 * `200 text/html`, `typeof data === "string"`.
 *
 * Същият договор като сондата за достъпност (`edgeSession.runReachabilityProbe`),
 * която пък гледа `ok === true && enabled === true`: от касата се признава само
 * отговор с правилната ФОРМА, не просто 200.
 */
function isEdgeAnswer(body: unknown): body is { synced: boolean; mode: EdgeMode } {
  if (typeof body !== "object" || body === null) return false;
  const { synced, mode } = body as { synced?: unknown; mode?: unknown };
  return typeof synced === "boolean" && (mode === "cloud" || mode === "local");
}

/** Нито ръчно задържане, нито „ти го натисна" — резервният път и старата каса. */
const NO_MANUAL_HOLD = { manualOverride: false, manualOverrideByThisDevice: false };

/** Двата endpoint-а носят една и съща истина под различни имена. */
function toSnapshot(
  state: { synced: boolean; mode: EdgeMode },
  unresolved: unknown,
  manualHold: Pick<
    EdgeSyncSnapshot,
    "manualOverride" | "manualOverrideByThisDevice"
  > = NO_MANUAL_HOLD,
): EdgeSyncSnapshot {
  return {
    synced: state.synced === true,
    mode: state.mode,
    unresolved: readUnresolvedCount(unresolved),
    ...manualHold,
  };
}
